import { ref } from "vue";
import api from "@/composables/api";
import { useUser } from "@/stores";

export default () => {
  const eventBuffer = ref([]);
  const isBuffering = ref(true);
  const bufferInterval = ref(null);

  // Initialize buffering on store creation
  const startBuffering = () => {
    if (bufferInterval.value) return;

    // Send events every minute
    bufferInterval.value = setInterval(() => {
      if (eventBuffer.value.length > 0) {
        sendEvents();
      }
    }, 60000); // 1 minute
  };

  const stopBuffering = () => {
    if (bufferInterval.value) {
      clearInterval(bufferInterval.value);
      bufferInterval.value = null;
    }
  };

  const addEvent = (event) => {
    const userStore = useUser();
    const user = userStore.user;

    // Add common metadata to event
    const enrichedEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
      userId: user?.uid || null,
      userEmail: user?.email || null,
      sessionId: generateSessionId(), // Simple session tracking
    };

    eventBuffer.value.push(enrichedEvent);
  };

  // Failed sends are re-queued; cap the buffer so a failing backend can't
  // grow it (and every retry payload) forever.
  const MAX_BUFFERED_EVENTS = 200;

  const sendEvents = async ({ keepalive = false } = {}) => {
    if (eventBuffer.value.length === 0) return;

    const events = [...eventBuffer.value];
    eventBuffer.value = []; // Clear buffer before sending to prevent duplicates

    try {
      // BE implemented POST /api/v1/analytics/events endpoint
      // Endpoint should accept an array of events in the following format:
      // {
      //   events: [{
      //     type: string,
      //     element: {
      //       // Base element properties
      //       text: string | null,      // Element's text content
      //       href: string | null,      // For links
      //       type: string | null,      // Form element type
      //       name: string | null,      // Element name attribute
      //       // Properties from data-track
      //       id: string,               // Menu item identifier
      //       type: string,             // e.g. 'main-menu', 'submenu-item'
      //       parent?: string,          // For submenu items
      //       href?: string             // Navigation target
      //     },
      //     timestamp: string,
      //     url: string,
      //     path: string,
      //     userId: string,
      //     userEmail: string,
      //     sessionId: string
      //   }]
      // }
      console.log("sending events", events);
      await api("v1/analytics/events", {
        method: "POST",
        body: { events },
        // background call: a 401 here must not bounce the visitor to /login
        skipAuthRedirect: true,
        // lets the request outlive the page when flushed on tab hide
        keepalive,
      });
    } catch (error) {
      console.error("Failed to send analytics events:", error);
      // Add events back to buffer on failure, keeping the most recent
      eventBuffer.value = [...events, ...eventBuffer.value].slice(
        -MAX_BUFFERED_EVENTS
      );
    }
  };

  // Generate a simple session ID - could be made more robust if needed
  const generateSessionId = () => {
    if (!window.sessionStorage.getItem("analytics_session_id")) {
      window.sessionStorage.setItem(
        "analytics_session_id",
        `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
      );
    }
    return window.sessionStorage.getItem("analytics_session_id");
  };

  // Start buffering when store is created
  startBuffering();

  // Flush when the tab is hidden (switching tabs, closing, navigating away).
  // Unlike `unload`, this fires reliably (incl. mobile) while the page can
  // still run async work, and doesn't disable the back/forward cache.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sendEvents({ keepalive: true });
    }
  });

  return {
    eventBuffer,
    addEvent,
    startBuffering,
    stopBuffering,
    sendEvents,
  };
};
